"use client";

import React, { useState, useEffect, useRef } from "react";
import { useScreens } from "@utils/component/useScreens/useScreens";
import { tagular } from "@cohesion/tagular";
import PropTypes from "prop-types";
import sampleSize from "lodash/sampleSize";
import bit from "@constants/best-in-travel/index.min";
import { preventLayoutShift } from "./utils";
import { MapPin } from "../../icons/mapPin";
import { Search } from "../../icons/search";
import { ArrowRight } from "../../icons/arrowRight";

/**
 * SearchPanel component for GlobalNav
 * Simplified version that doesn't rely on typesense
 */
export function SearchPanel({ disableAlert = false }) {
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [alertDismissed, setAlertDismissed] = useState(disableAlert);
  const [featured, setFeatured] = useState([]);
  const [mounted, setMounted] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const { screen } = useScreens();
  const isDesktop = screen === "lg" || screen === "xl" || screen === "2xl";
  const searchPanelRef = useRef(null);
  const searchTriggerRef = useRef(null);
  // Use a ref to track the current state of isPanelOpen without adding it as a dependency
  // to useEffect. This prevents unnecessary effect re-runs while ensuring event handlers
  // always have access to the latest value, avoiding issues with stale closures.
  const isPanelOpenRef = useRef(isPanelOpen);

  useEffect(() => {
    isPanelOpenRef.current = isPanelOpen;
  }, [isPanelOpen]);

  // Mount effect - runs only on client
  useEffect(() => {
    setMounted(true);

    // Define handleAlertDismissed at the useEffect scope level so it's available in cleanup
    const handleAlertDismissed = () => {
      setAlertDismissed(true);
    };

    // Handler for when mobile menu is opened
    const handleCloseSearchPanel = () => {
      if (isPanelOpenRef.current) {
        setIsPanelOpen(false);
        // Reset body overflow and padding
        preventLayoutShift(false);
      }
    };

    // Only check for alert dismissal if not disabled
    if (!disableAlert) {
      document.addEventListener("alertDismissed", handleAlertDismissed);
    }

    // Add listener for closing search panel when mobile menu opens
    document.addEventListener("closeSearchPanel", handleCloseSearchPanel);

    // Get featured destinations from Best in Travel data, using similar approach to Astro component
    try {
      const selectedDestinations = sampleSize(
        bit.destinations.map(d => ({
          destination: d.name,
          url: d.slug,
        })),
        3
      );
      setFeatured(selectedDestinations);
    } catch (error) {
      // Fallback to random destinations if data is unavailable
      // eslint-disable-next-line no-console
      console.warn(
        "Could not load Best in Travel data for search panel",
        error
      );
      setFeatured([
        { destination: "Benin", url: "/destinations/benin" },
        { destination: "Chile", url: "/destinations/chile" },
        { destination: "Morocco", url: "/destinations/morocco" },
      ]);
    }

    return () => {
      if (!disableAlert) {
        document.removeEventListener("alertDismissed", handleAlertDismissed);
      }
      document.removeEventListener("closeSearchPanel", handleCloseSearchPanel);
    };
  }, [disableAlert]); // Removed isPanelOpen from dependencies

  // Handle body overflow and add padding when the panel is open/hovered to prevent layout shifts
  useEffect(() => {
    if (mounted) {
      // For desktop, use isHovering state; for mobile, use isPanelOpen state
      const shouldLockScroll = isDesktop ? isHovering : isPanelOpen;
      preventLayoutShift(shouldLockScroll);

      return () => {
        preventLayoutShift(false);
      };
    }

    return () => {};
  }, [isHovering, isPanelOpen, isDesktop, mounted]);

  // Get panel top position based on alert status
  const getPanelTopPosition = () => {
    const isAlertHidden = disableAlert || alertDismissed;

    if (isDesktop) {
      return isAlertHidden ? "top-20" : "top-32";
    }
    return isAlertHidden ? "top-[72px]" : "top-[126px]";
  };

  // Toggle panel for mobile
  const togglePanel = e => {
    if (!isDesktop) {
      e.preventDefault();
      setIsPanelOpen(!isPanelOpen);

      // Create and dispatch a custom event to communicate with the GlobalNav component
      try {
        const closeMobileMenuEvent = new CustomEvent("closeMobileMenu", {
          detail: {
            openSearchPanel: true,
          },
        });
        document.dispatchEvent(closeMobileMenuEvent);
      } catch (error) {
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.error("Failed to dispatch 'closeMobileMenu' event:", error);
        }
      }

      // Track panel toggle - match Astro implementation exactly
      tagular("click", {
        actionOutcome: !isPanelOpen ? "OPEN" : "CLOSE",
        outboundUrl: "null",
        webElement: {
          location: "GLOBAL NAV",
          elementType: "BUTTON",
          position: "SEARCH",
          text: "SEARCH",
        },
      });
    }
  };

  // Close panel when clicking outside (mobile)
  useEffect(() => {
    let cleanup = () => {};

    if (mounted) {
      const handleClickOutside = e => {
        if (!isDesktop && isPanelOpen) {
          if (
            searchPanelRef.current &&
            searchTriggerRef.current &&
            !searchPanelRef.current.contains(e.target) &&
            !searchTriggerRef.current.contains(e.target)
          ) {
            setIsPanelOpen(false);
          }
        }
      };

      document.addEventListener("click", handleClickOutside);

      cleanup = function cleanupClickOutside() {
        document.removeEventListener("click", handleClickOutside);
      };
    }

    return cleanup;
  }, [isPanelOpen, isDesktop, mounted]);

  const handleSubmit = e => {
    e.preventDefault();
    const query = e.target.querySelector("input").value;
    if (query) {
      // Track search query submission - match Astro implementation exactly
      tagular("search", {
        event: "SearchExecuted",
        searchContext: {
          query,
          searchType: "DESTINATION",
        },
        webElement: {
          location: "GLOBAL NAV",
          elementType: "SEARCHBOX",
          position: "SEARCH PANEL",
        },
      });

      window.location.href = `/search?q=${encodeURIComponent(query)}`;
    }
  };

  // Return just the search icon until the component is mounted on client
  if (!mounted) {
    return (
      <div className="relative group lg:px-8 lg:py-2">
        <button
          type="button"
          className="flex items-center justify-center p-2 transition-colors rounded-full bg-black-100 hover:bg-blue hover:text-white lg:bg-transparent lg:p-0 lg:hover:bg-transparent lg:hover:text-blue"
          aria-label="Search"
        >
          <Search
            className="inline-flex"
            width="20"
            height="20"
            aria-hidden="true"
          />
          <span className="sr-only">Search</span>
        </button>
      </div>
    );
  }

  return (
    <div
      className="relative group lg:px-8 lg:py-2 lg:after:content-[''] lg:after:absolute lg:after:top-0 lg:after:left-0 lg:after:right-0 lg:after:h-full lg:after:bg-transparent lg:after:pointer-events-none lg:after:z-[1] lg:hover:after:h-[100vh] lg:hover:after:pointer-events-auto"
      onMouseEnter={() => isDesktop && setIsHovering(true)}
      onMouseLeave={() => isDesktop && setIsHovering(false)}
    >
      <button
        ref={searchTriggerRef}
        type="button"
        className="flex items-center justify-center p-2 transition-colors rounded-full text-black hover:text-blue lg:bg-transparent lg:p-0 lg:hover:bg-transparent lg:hover:text-blue lg:relative lg:z-[2]"
        aria-label="Search"
        aria-expanded={isPanelOpen}
        aria-controls="searchPanel"
        aria-haspopup="dialog"
        onClick={togglePanel}
      >
        <Search
          className="inline-flex"
          width="20"
          height="20"
          aria-hidden="true"
        />
        <span className="sr-only">Search</span>
      </button>

      {/* Desktop panel (visible on hover) and Mobile panel (visible on click) */}
      <div
        ref={searchPanelRef}
        className={`
          ${
            isDesktop
              ? `fixed z-50 left-1/2 transform -translate-x-1/2 max-w-[1440px] w-[calc(100%-32px)] bg-white shadow-lg transition-all duration-200 invisible opacity-0 group-hover:visible group-hover:opacity-100 ${getPanelTopPosition()}`
              : `fixed z-50 left-0 right-0 mx-4 shadow-md bg-white transition-all duration-200 ${getPanelTopPosition()} ${
                  isPanelOpen
                    ? "visible opacity-100 translate-y-0"
                    : "invisible opacity-0 -translate-y-5"
                }`
          }`}
        role="dialog"
        aria-modal="true"
        aria-label="Search panel"
      >
        <div className="w-full">
          <div className="grid grid-cols-12 min-h-[400px]">
            <div className="col-span-12 p-6">
              {/* Search Input */}
              <div className="flex justify-center w-full gap-x-4">
                <div className="relative w-full">
                  <form
                    onSubmit={handleSubmit}
                    className="relative"
                    role="search"
                  >
                    <div className="relative">
                      <input
                        id="search-input"
                        type="search"
                        name="q"
                        className="block w-full py-2 pl-2 pr-8 text-lg font-light leading-normal bg-white border-b border-solid appearance-none focus:outline-none border-black-200 placeholder:text-black-200"
                        placeholder="Search destinations"
                        aria-label="Search destinations"
                        autoComplete="off"
                      />
                      <button
                        type="submit"
                        className="absolute top-0 bottom-0 pl-8 my-auto right-4 text-md"
                        aria-label="Search"
                        onClick={() => {
                          // Track search button click - match Astro implementation exactly
                          tagular("click", {
                            actionOutcome: "SEARCH",
                            outboundUrl: "null",
                            webElement: {
                              location: "GLOBAL NAV",
                              elementType: "BUTTON",
                              position: "SEARCH PANEL",
                              text: "SEARCH",
                            },
                          });
                        }}
                      >
                        <Search
                          className="inline-block text-blue"
                          width="15"
                          height="15"
                        />
                      </button>
                    </div>
                  </form>
                </div>
              </div>

              {/* Featured destinations */}
              <div className="mt-6">
                <a href="/best-in-travel" className="text-sm font-semibold">
                  Explore Best in Travel 2025
                </a>
                <ul className="mt-4">
                  {featured.map(({ destination, url }, index) => (
                    <li key={destination}>
                      <a
                        href={url}
                        className="flex items-center my-2 text-lg text-black hover:text-blue-400"
                        onClick={() => {
                          // Track featured destination click - match Astro implementation exactly
                          tagular("click", {
                            actionOutcome: "INTERNALLINK",
                            outboundUrl: url,
                            webElement: {
                              location: "GLOBAL NAV",
                              elementType: "LINK",
                              position: `FEATURED-${index + 1}`,
                              text: destination.toUpperCase(),
                            },
                          });
                        }}
                      >
                        <MapPin
                          height="1em"
                          className="mr-3 text-sm text-blue"
                          style={{ fill: "currentColor" }}
                          aria-hidden="true"
                        />
                        <span>{destination}</span>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Footer link */}
          <a
            href="/search"
            className="flex bg-black-100 gap-x-2.5 items-center py-3 px-6 font-semibold text-black hover:bg-blue hover:text-white"
            onClick={() => {
              // Track "Search all" link click - match Astro implementation exactly
              tagular("click", {
                actionOutcome: "INTERNALLINK",
                outboundUrl: "/search",
                webElement: {
                  location: "GLOBAL NAV",
                  elementType: "LINK",
                  position: "SEARCH PANEL FOOTER",
                  text: "SEARCH ALL OF LONELY PLANET",
                },
              });
            }}
          >
            Search all of Lonely Planet
            <ArrowRight width="16" height="16" aria-hidden="true" />
          </a>
        </div>
      </div>
    </div>
  );
}

SearchPanel.propTypes = {
  disableAlert: PropTypes.bool,
};

export default SearchPanel;
